import React, { useMemo, useState } from 'react';
import {
	ConversationMember,
	MessageAttachment,
	useLazyConversationDetailQuery,
	useLazyConversationMessagesQuery,
	User,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import styles from './ActionCenterMessageDetail.module.scss';
import BackButton from '../../../../Components/Button/BackButton';
import Text from '../../../../Components/Base/Text/Text';
import ActionCenterMessageRecipients from './ActionCenterMessageRecipients';
import ReplyButton from '../../../../Components/Button/ReplyButton';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import ActionCenterReplyForm from '../ActionCenterReplyForm/ActionCenterReplyForm';
import ActionCenterConversationDetailMessageList from '../ActionCenterConversationMessageList/ActionCenterConversationMessageList';
import ActionCenterConversationMessageAttachment from '../ActionCenterConversationMessageList/ActionCenterConversationMessageAttachment';
import { useEffectAsync } from '../../../../helpers';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import { findAndReplaceLinksInText } from '../../../Base/Helpers/text.helper';
import NoEscape from '../../../../Components/NoEscape';
import { ConversationListType } from '../../Enums/ConversationListType.enum';
import { useAppContext } from '../../../../Services/AppContext';

interface Props {
	conversationId: string;
	onBack?: () => void;
	type?: ConversationListType;
}

const ActionCenterMessageDetail: React.FC<Props> = ({
	conversationId,
	onBack,
	type = ConversationListType.INBOX,
}) => {
	const [getConversationDetail, { data }] = useLazyConversationDetailQuery();
	const [getMessages, { data: messagesData }] =
		useLazyConversationMessagesQuery();
	const [isReplayMode, setIsReplyMode] = useState(false);
	const [isRespondToAll, setIsRespondToAll] = useState(false);
	const conversation = data?.conversation;
	const messages = messagesData?.messages?.data;
	const [isLoading, setIsLoading] = useState(false);
	const { userData } = useAppContext();

	const loadData = async () => {
		setIsLoading(true);
		await getConversationDetail({ id: conversationId });
		await getMessages({ conversationId });
		setIsLoading(false);
	};

	useEffectAsync(async () => {
		await loadData();
	}, []);

	const handleAfterReply = async () => {
		setIsReplyMode(false);
		await loadData();
	};

	const handleRespond = () => {
		setIsRespondToAll(false);
		setIsReplyMode(true);
	};

	const handleRespondToAll = () => {
		setIsRespondToAll(true);
		setIsReplyMode(true);
	};

	const lastSender = useMemo(() => {
		const sortedMessages = [...(messages ?? [])].sort(
			(a, b) =>
				new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
		);
		if (type === ConversationListType.INBOX) {
			return sortedMessages.find(message => {
				const senderUser = message.sender?.user;
				return senderUser?.id && senderUser.id !== userData.id;
			})?.sender?.user as User | undefined;
		}
		if (type === ConversationListType.SENT) {
			return sortedMessages.find(message => {
				const senderUser = message.sender?.user;
				return senderUser?.id && senderUser.id === userData.id;
			})?.sender?.user as User | undefined;
		}
		return sortedMessages.find(message => message.sender?.user?.id)?.sender
			?.user as User | undefined;
	}, [messages, type, userData.id]);

	if (!conversation || isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				<div className={styles.controls1}>
					{onBack && <BackButton onClick={onBack} />}
				</div>
				<div className={styles.content}>
					<div className={styles.from}>
						<Text tx='base.shared.from' firstUpper inline />:{' '}
						{lastSender?.profile?.fullName}
					</div>
					<ActionCenterMessageRecipients
						conversationMembers={(
							conversation.conversationMembers as ConversationMember[]
						).map(conversationMember => conversationMember.user)}
						sender={lastSender}
					/>
					<div className={styles.title}>{conversation.name}</div>
				</div>
				<div className={styles.date}>
					<FormattedMomentDate
						date={conversation.lastMessage?.createdAt}
						format={DateFormat.DATE_TIME}
					/>
				</div>
				<div className={styles.controls2}>
					<ReplyButton onClick={handleRespond} />
					<ReplyButton onClick={handleRespondToAll} title='base.respondAll' />
				</div>
			</div>
			<div className={styles.actual}>
				<NoEscape>
					{findAndReplaceLinksInText(conversation.lastMessage?.content?.text)}
				</NoEscape>
				{(conversation.lastMessage?.messageAttachments?.length || 0) > 0 && (
					<div className={styles.attachments}>
						<div className={styles.title}>
							<Text tx='base.attachments' inline firstUpper />:
						</div>
						{conversation.lastMessage?.messageAttachments?.map(
							(attachment, key) => (
								<ActionCenterConversationMessageAttachment
									item={attachment as MessageAttachment}
									key={attachment?.id || key}
								/>
							),
						)}
					</div>
				)}
				<div className={styles.formWrapper}>
					{isReplayMode && conversation.lastMessage?.id ? (
						<ActionCenterReplyForm
							onCancel={() => setIsReplyMode(false)}
							conversationId={conversationId}
							onSuccess={handleAfterReply}
							respondToAll={isRespondToAll}
							lastMessageId={conversation.lastMessage.id}
						/>
					) : (
						<div className={styles.respondButtons}>
							<Button
								theme={Theme.BLUE}
								title='base.respond'
								className={styles.btn}
								onClick={handleRespond}
							/>
							<Button
								theme={Theme.BLUE}
								title='base.respondAll'
								className={styles.btn}
								onClick={handleRespondToAll}
							/>
						</div>
					)}
				</div>
			</div>
			<ActionCenterConversationDetailMessageList
				conversationId={conversation.id}
			/>
		</div>
	);
};

export default ActionCenterMessageDetail;
